import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { fetchBookingDetails } from "../../../redux/features/booking/bookingSlice.tsx";
import { useAppDispatch } from "../../../hooks/useAppDispatch.tsx";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const BookingDetails = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const { bookingDetails, loading, error } = useSelector(
    (state: RootState) => state.bookings
  );

  const bookingData = bookingDetails?.data;

  // Custom arrow components
  const CustomPrevArrow = (props: any) => {
    const { onClick } = props;
    return (
      <Box
        onClick={onClick}
        sx={{
          position: "absolute",
          top: "50%",
          left: "10px",
          zIndex: 1,
          cursor: "pointer",
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          borderRadius: "50%",
          padding: "10px",
        }}
      >
        <ArrowBackIosIcon />
      </Box>
    );
  };

  const CustomNextArrow = (props: any) => {
    const { onClick } = props;
    return (
      <Box
        onClick={onClick}
        sx={{
          position: "absolute",
          top: "50%",
          right: "10px",
          zIndex: 1,
          cursor: "pointer",
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          borderRadius: "50%",
          padding: "10px",
        }}
      >
        <ArrowBackIosIcon sx={{ transform: "rotate(180deg)" }} />
      </Box>
    );
  };

  // Carousel settings
  const settings = useMemo(() => {
    const hasMultipleImages =
      bookingData?.images && bookingData.images.length > 1;

    return {
      dots: hasMultipleImages,
      infinite: hasMultipleImages,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      prevArrow: hasMultipleImages ? <CustomPrevArrow /> : <></>,
      nextArrow: hasMultipleImages ? <CustomNextArrow /> : <></>,
      // Disable dragging/swiping for single images
      swipe: hasMultipleImages,
      draggable: hasMultipleImages,
      // Hide arrows on mobile for single images
      responsive: [
        {
          breakpoint: 768,
          settings: {
            arrows: hasMultipleImages,
          },
        },
      ],
    };
  }, [bookingData?.images]);

  useEffect(() => {
    if (id) {
      const bookingId = parseInt(id, 10);
      if (!isNaN(bookingId)) {
        dispatch(fetchBookingDetails(bookingId));
      } else {
        console.error("Invalid booking ID:", id);
      }
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (error && error.status === 500) {
      navigate("/not-found");
    }
  }, [error, navigate]);

  const handleGoBack = () => {
    navigate("/booking-management");
  };

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getUTCDate();
    const monthIndex = date.getUTCMonth();
    const year = date.getUTCFullYear();
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[monthIndex];
    return `${day} ${month} ${year}`;
  }

  function formatGuests(guests: {
    adults: number | null;
    children: number | null;
    babies: number | null;
  }): string {
    // Create an array to hold the parts of the guest description
    const parts: string[] = [];

    // Conditionally add each part to the array if it's not null
    if (guests?.adults !== null && guests?.adults !== undefined) {
      parts.push(guests.adults === 1 ? "1 adult" : `${guests.adults} adults`);
    }

    if (guests?.children !== null && guests?.children !== undefined) {
      parts.push(
        guests.children === 1 ? "1 child" : `${guests.children} children`
      );
    }

    if (guests?.babies !== null && guests?.babies !== undefined) {
      parts.push(guests.babies === 1 ? "1 baby" : `${guests.babies} babies`);
    }

    // Join the non-null parts with a comma and space
    return parts.join(", ");
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const getImageSrc = (imageUrl?: string) => {
    if (!imageUrl) {
      return "/assets/no-image.png";
    }
    return `${process.env.REACT_APP_AWS_CLOUD_FRONT || ""}${imageUrl}`;
  };

  return (
    <Box sx={{ padding: "20px", margin: "0 auto" }}>
      <Typography variant="h5" fontWeight="bold" sx={{ marginBottom: "20px" }}>
        <Button
          variant="text"
          onClick={handleGoBack}
          sx={{ fontWeight: "normal" }}
        >
          <ArrowBackIosIcon />
        </Button>
        Booking Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {bookingData?.images && bookingData.images.length > 0 ? (
              bookingData.images.length === 1 ? (
                // Single image display without slider
                <Box
                  sx={{
                    width: "100%",
                    height: "400px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={getImageSrc(bookingData?.images[0])}
                    alt="booking Image"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "10px",
                    }}
                  />
                </Box>
              ) : (
                <Slider {...settings}>
                  {bookingData.images.map((image: string, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        width: "100%",
                        height: "400px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={getImageSrc(image)}
                        alt={`Property Image ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: "10px",
                        }}
                      />
                    </Box>
                  ))}
                </Slider>
              )
            ) : (
              <div className="w-full flex justify-center">
                <img
                  src="/assets/no-image.png"
                  alt="properties"
                  style={{
                    filter: "grayscale(100%)",
                    width: "50%",
                    height: "50%",
                    borderRadius: "10px",
                  }}
                />
              </div>
            )}
            <div>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#979797",
                  marginBottom: "8px",
                }}
              >
                Property
              </Typography>

              <Typography
                variant="h6"
                sx={{ fontSize: "24px", fontWeight: "700", color: "#3D3A3A" }}
              >
                {bookingData?.unittypecode}
              </Typography>
            </div>
            <div>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#979797",
                  marginBottom: "8px",
                }}
              >
                About
              </Typography>
              {/* <Typography
                variant="body2"
                sx={{ fontWeight: "400", fontSize: "16px", color: "#3D3A3A" }}
              >
                {bookingData?.description}
              </Typography> */}
              <div
                // variant="body2"
                // sx={{ fontWeight: "400", fontSize: "16px", color: "#3D3A3A" }}
                className="text-gray-600 htmlContent"
                dangerouslySetInnerHTML={{ __html: bookingData?.description }}
              />
            </div>
          </div>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={1}
            sx={{
              borderRadius: "10px",
              padding: "24px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ marginBottom: "20px", fontSize: "24px", fontWeight: "400" }}
            >
              Basic Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Unit Name
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {bookingData?.unittypecode}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Booked user name
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {bookingData?.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Check in Date
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {formatDate(bookingData?.fromdate)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Check out Date
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {formatDate(bookingData?.todate)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Total Nights
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {bookingData?.noofnights}{" "}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Total Guests
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {formatGuests(bookingData?.guests)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {" "}
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Remarks
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "400",
                    color: "#3D3A3A",
                    fontSize: { xs: "19px", md: "24px" },
                  }}
                >
                  {bookingData?.userremarks}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "16px", fontWeight: "400", color: "#979797" }}
                >
                  Status
                </Typography>
                <Typography
                  sx={{
                    color:
                      bookingData?.status === "Rejected"
                        ? "error.main"
                        : "success.main",
                    fontWeight: "bold",
                  }}
                >
                  {bookingData?.status}
                </Typography>
              </Grid>
              <Grid item xs={12}></Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookingDetails;
